// Auto-generated by generate-firestore-wrappers.js
import {
    createCollectionApi,
    createCollectionGroupApi,
    createDocumentApi,
    paths,
    useCollection,
    useCollectionGroup,
    useDocument,
} from "./firestoreBaseApi";

// ---- Generic Path-Based Helpers ----
// Cache API instances per path to ensure stable references.
// Recreating these on every render breaks hook dependency equality
// (e.g. useEffect deps), causing unnecessary re-fetches and rerenders.

const documentApiCache = new Map();
if (documentApiCache.size > 1000) {
    documentApiCache.clear();
}

function DocumentByPath(path) {
    if (!documentApiCache.has(path)) {
        const segments = path.split("/").filter(Boolean);

        const api = createDocumentApi(() => segments)();
        documentApiCache.set(path, api);
    }

    return documentApiCache.get(path);
}

/**
 * Hook for subscribing to—or once-fetching—a single document by full path.
 *
 * @param path   Firestore document path (e.g. "apps/123")
 * @param options.mode 'live' (default) or 'once'
 */
export function useDocumentByPath(path, options) {
    return useDocument(DocumentByPath, [path], options);
}

const collectionApiCache = new Map();
if (collectionApiCache.size > 1000) {
    collectionApiCache.clear();
}

function CollectionByPath(path) {
    if (!collectionApiCache.has(path)) {
        const segments = path.split("/").filter(Boolean);

        const api = createCollectionApi(() => segments)();
        collectionApiCache.set(path, api);
    }

    return collectionApiCache.get(path);
}

/**
 * Hook for subscribing to—or once-fetching—a collection by full path.
 *
 * @param path         Firestore collection path (e.g. "apps")
 * @param constraints  Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useCollectionByPath(path, constraints = [], options = {}) {
    return useCollection(CollectionByPath, [path], constraints, options);
}

const collectionGroupApiCache = new Map();
if (collectionGroupApiCache.size > 1000) {
    collectionGroupApiCache.clear();
}

function CollectionGroupById(collectionId) {
    if (!collectionGroupApiCache.has(collectionId)) {
        const api = createCollectionGroupApi(collectionId);
        collectionGroupApiCache.set(collectionId, api);
    }

    return collectionGroupApiCache.get(collectionId);
}

/**
 * Hook for subscribing to—or once-fetching—a collection group by collection id.
 *
 * @param collectionId Collection group id (e.g. "apps")
 * @param constraints  Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useCollectionGroupById(
    collectionId,
    constraints = [],
    options = {},
) {
    return useCollectionGroup(
        CollectionGroupById(collectionId),
        constraints,
        options,
    );
}

// ---- App Resources ----

export function AppDocument(appId) {
    return createDocumentApi(paths.appDocument)(appId);
}

/**
 * Hook for subscribing to or once-fetching a single App document.
 *
 * @param appId
 * @param options.mode 'live' (default) or 'once'
 */
export function useAppDocument(appId, options) {
    return useDocument(AppDocument, [appId], options);
}

export function AppCollection() {
    return createCollectionApi(paths.appCollection)();
}

/**
 * Hook for subscribing to or once-fetching a App collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useAppCollection(constraints = [], options = {}) {
    return useCollection(AppCollection, [], constraints, options);
}

export const AppCollectionGroup = createCollectionGroupApi("apps");

/**
 * Hook for subscribing to or once-fetching a App collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useAppCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(AppCollectionGroup, constraints, options);
}

// ---- Areas Resources ----

export function AreasDocument(orgId, locId) {
    return createDocumentApi(paths.areasDocument)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a single Areas document.
 *
 * @param orgId
 * @param locId
 * @param options.mode 'live' (default) or 'once'
 */
export function useAreasDocument(orgId, locId, options) {
    return useDocument(AreasDocument, [orgId, locId], options);
}

// ---- Batches Resources ----

export function BatchesDocument(orgId, locId, batchId) {
    return createDocumentApi(paths.batchesDocument)(orgId, locId, batchId);
}

/**
 * Hook for subscribing to or once-fetching a single Batches document.
 *
 * @param orgId
 * @param locId
 * @param batchId
 * @param options.mode 'live' (default) or 'once'
 */
export function useBatchesDocument(orgId, locId, batchId, options) {
    return useDocument(BatchesDocument, [orgId, locId, batchId], options);
}

export function BatchesCollection(orgId, locId) {
    return createCollectionApi(paths.batchesCollection)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a Batches collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useBatchesCollection(
    orgId,
    locId,
    constraints = [],
    options = {},
) {
    return useCollection(
        BatchesCollection,
        [orgId, locId],
        constraints,
        options,
    );
}

export const BatchesCollectionGroup = createCollectionGroupApi("batches");

/**
 * Hook for subscribing to or once-fetching a Batches collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useBatchesCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(BatchesCollectionGroup, constraints, options);
}

// ---- Changelog Resources ----

export function ChangelogDocument(docId) {
    return createDocumentApi(paths.changelogDocument)(docId);
}

/**
 * Hook for subscribing to or once-fetching a single Changelog document.
 *
 * @param docId
 * @param options.mode 'live' (default) or 'once'
 */
export function useChangelogDocument(docId, options) {
    return useDocument(ChangelogDocument, [docId], options);
}

export function ChangelogCollection() {
    return createCollectionApi(paths.changelogCollection)();
}

/**
 * Hook for subscribing to or once-fetching a Changelog collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useChangelogCollection(constraints = [], options = {}) {
    return useCollection(ChangelogCollection, [], constraints, options);
}

export const ChangelogCollectionGroup = createCollectionGroupApi("changelogs");

/**
 * Hook for subscribing to or once-fetching a Changelog collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useChangelogCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(ChangelogCollectionGroup, constraints, options);
}

// ---- Dock Resources ----

export function DockDocument(orgId, locId, dockId) {
    return createDocumentApi(paths.dockDocument)(orgId, locId, dockId);
}

/**
 * Hook for subscribing to or once-fetching a single Dock document.
 *
 * @param orgId
 * @param locId
 * @param dockId
 * @param options.mode 'live' (default) or 'once'
 */
export function useDockDocument(orgId, locId, dockId, options) {
    return useDocument(DockDocument, [orgId, locId, dockId], options);
}

export function DockCollection(orgId, locId) {
    return createCollectionApi(paths.dockCollection)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a Dock collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useDockCollection(
    orgId,
    locId,
    constraints = [],
    options = {},
) {
    return useCollection(DockCollection, [orgId, locId], constraints, options);
}

export const DockCollectionGroup = createCollectionGroupApi("docks");

/**
 * Hook for subscribing to or once-fetching a Dock collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useDockCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(DockCollectionGroup, constraints, options);
}

// ---- DroneCommand Resources ----

export function DroneCommandDocument(droneId) {
    return createDocumentApi(paths.droneCommandDocument)(droneId);
}

/**
 * Hook for subscribing to or once-fetching a single DroneCommand document.
 *
 * @param droneId
 * @param options.mode 'live' (default) or 'once'
 */
export function useDroneCommandDocument(droneId, options) {
    return useDocument(DroneCommandDocument, [droneId], options);
}

export function DroneCommandCollection() {
    return createCollectionApi(paths.droneCommandCollection)();
}

/**
 * Hook for subscribing to or once-fetching a DroneCommand collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useDroneCommandCollection(constraints = [], options = {}) {
    return useCollection(DroneCommandCollection, [], constraints, options);
}

export const DroneCommandCollectionGroup =
    createCollectionGroupApi("droneCommands");

/**
 * Hook for subscribing to or once-fetching a DroneCommand collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useDroneCommandCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(
        DroneCommandCollectionGroup,
        constraints,
        options,
    );
}

// ---- DroneConfig Resources ----

export function DroneConfigDocument(droneId) {
    return createDocumentApi(paths.droneConfigDocument)(droneId);
}

/**
 * Hook for subscribing to or once-fetching a single DroneConfig document.
 *
 * @param droneId
 * @param options.mode 'live' (default) or 'once'
 */
export function useDroneConfigDocument(droneId, options) {
    return useDocument(DroneConfigDocument, [droneId], options);
}

export function DroneConfigCollection() {
    return createCollectionApi(paths.droneConfigCollection)();
}

/**
 * Hook for subscribing to or once-fetching a DroneConfig collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useDroneConfigCollection(constraints = [], options = {}) {
    return useCollection(DroneConfigCollection, [], constraints, options);
}

export const DroneConfigCollectionGroup =
    createCollectionGroupApi("droneConfigs");

/**
 * Hook for subscribing to or once-fetching a DroneConfig collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useDroneConfigCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(DroneConfigCollectionGroup, constraints, options);
}

// ---- FeedbackOptions Resources ----

export function FeedbackOptionsDocument() {
    return createDocumentApi(paths.feedbackOptionsDocument)();
}

/**
 * Hook for subscribing to or once-fetching a single FeedbackOptions document.
 *

 * @param options.mode 'live' (default) or 'once'
 */
export function useFeedbackOptionsDocument(options) {
    return useDocument(FeedbackOptionsDocument, [], options);
}

// ---- Flight Resources ----

export function FlightDocument(orgId, locId, flightId) {
    return createDocumentApi(paths.flightDocument)(orgId, locId, flightId);
}

/**
 * Hook for subscribing to or once-fetching a single Flight document.
 *
 * @param orgId
 * @param locId
 * @param flightId
 * @param options.mode 'live' (default) or 'once'
 */
export function useFlightDocument(orgId, locId, flightId, options) {
    return useDocument(FlightDocument, [orgId, locId, flightId], options);
}

export function FlightCollection(orgId, locId) {
    return createCollectionApi(paths.flightCollection)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a Flight collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useFlightCollection(
    orgId,
    locId,
    constraints = [],
    options = {},
) {
    return useCollection(
        FlightCollection,
        [orgId, locId],
        constraints,
        options,
    );
}

export const FlightCollectionGroup = createCollectionGroupApi("flights");

/**
 * Hook for subscribing to or once-fetching a Flight collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useFlightCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(FlightCollectionGroup, constraints, options);
}

// ---- Flyzones Resources ----

export function FlyzonesDocument(orgId, locId) {
    return createDocumentApi(paths.flyzonesDocument)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a single Flyzones document.
 *
 * @param orgId
 * @param locId
 * @param options.mode 'live' (default) or 'once'
 */
export function useFlyzonesDocument(orgId, locId, options) {
    return useDocument(FlyzonesDocument, [orgId, locId], options);
}

// ---- Livestream Resources ----

export function LivestreamDocument(droneId) {
    return createDocumentApi(paths.livestreamDocument)(droneId);
}

/**
 * Hook for subscribing to or once-fetching a single Livestream document.
 *
 * @param droneId
 * @param options.mode 'live' (default) or 'once'
 */
export function useLivestreamDocument(droneId, options) {
    return useDocument(LivestreamDocument, [droneId], options);
}

export function LivestreamCollection() {
    return createCollectionApi(paths.livestreamCollection)();
}

/**
 * Hook for subscribing to or once-fetching a Livestream collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useLivestreamCollection(constraints = [], options = {}) {
    return useCollection(LivestreamCollection, [], constraints, options);
}

export const LivestreamCollectionGroup =
    createCollectionGroupApi("livestreams");

/**
 * Hook for subscribing to or once-fetching a Livestream collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useLivestreamCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(LivestreamCollectionGroup, constraints, options);
}

// ---- Location Resources ----

export function LocationDocument(orgId, locId) {
    return createDocumentApi(paths.locationDocument)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a single Location document.
 *
 * @param orgId
 * @param locId
 * @param options.mode 'live' (default) or 'once'
 */
export function useLocationDocument(orgId, locId, options) {
    return useDocument(LocationDocument, [orgId, locId], options);
}

export function LocationCollection(orgId) {
    return createCollectionApi(paths.locationCollection)(orgId);
}

/**
 * Hook for subscribing to or once-fetching a Location collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useLocationCollection(orgId, constraints = [], options = {}) {
    return useCollection(LocationCollection, [orgId], constraints, options);
}

export const LocationCollectionGroup = createCollectionGroupApi("locations");

/**
 * Hook for subscribing to or once-fetching a Location collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useLocationCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(LocationCollectionGroup, constraints, options);
}

// ---- LocationBuilderAreas Resources ----

export function LocationBuilderAreasDocument(orgId, locId) {
    return createDocumentApi(paths.locationBuilderAreasDocument)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a single LocationBuilderAreas document.
 *
 * @param orgId
 * @param locId
 * @param options.mode 'live' (default) or 'once'
 */
export function useLocationBuilderAreasDocument(orgId, locId, options) {
    return useDocument(LocationBuilderAreasDocument, [orgId, locId], options);
}

// ---- Markers Resources ----

export function MarkersDocument(orgId, locId) {
    return createDocumentApi(paths.markersDocument)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a single Markers document.
 *
 * @param orgId
 * @param locId
 * @param options.mode 'live' (default) or 'once'
 */
export function useMarkersDocument(orgId, locId, options) {
    return useDocument(MarkersDocument, [orgId, locId], options);
}

// ---- Measurement Resources ----

export function MeasurementDocument(orgId, locId, measurementId) {
    return createDocumentApi(paths.measurementDocument)(
        orgId,
        locId,
        measurementId,
    );
}

/**
 * Hook for subscribing to or once-fetching a single Measurement document.
 *
 * @param orgId
 * @param locId
 * @param measurementId
 * @param options.mode 'live' (default) or 'once'
 */
export function useMeasurementDocument(orgId, locId, measurementId, options) {
    return useDocument(
        MeasurementDocument,
        [orgId, locId, measurementId],
        options,
    );
}

export function MeasurementCollection(orgId, locId) {
    return createCollectionApi(paths.measurementCollection)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a Measurement collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useMeasurementCollection(
    orgId,
    locId,
    constraints = [],
    options = {},
) {
    return useCollection(
        MeasurementCollection,
        [orgId, locId],
        constraints,
        options,
    );
}

export const MeasurementCollectionGroup =
    createCollectionGroupApi("measurements");

/**
 * Hook for subscribing to or once-fetching a Measurement collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useMeasurementCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(MeasurementCollectionGroup, constraints, options);
}

// ---- Mission Resources ----

export function MissionDocument(orgId, locId, missionId) {
    return createDocumentApi(paths.missionDocument)(orgId, locId, missionId);
}

/**
 * Hook for subscribing to or once-fetching a single Mission document.
 *
 * @param orgId
 * @param locId
 * @param missionId
 * @param options.mode 'live' (default) or 'once'
 */
export function useMissionDocument(orgId, locId, missionId, options) {
    return useDocument(MissionDocument, [orgId, locId, missionId], options);
}

export function MissionCollection(orgId, locId) {
    return createCollectionApi(paths.missionCollection)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a Mission collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useMissionCollection(
    orgId,
    locId,
    constraints = [],
    options = {},
) {
    return useCollection(
        MissionCollection,
        [orgId, locId],
        constraints,
        options,
    );
}

export const MissionCollectionGroup = createCollectionGroupApi("missions");

/**
 * Hook for subscribing to or once-fetching a Mission collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useMissionCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(MissionCollectionGroup, constraints, options);
}

// ---- MissionHandlerCluster Resources ----

export function MissionHandlerClusterDocument(clusterName) {
    return createDocumentApi(paths.missionHandlerClusterDocument)(clusterName);
}

/**
 * Hook for subscribing to or once-fetching a single MissionHandlerCluster document.
 *
 * @param clusterName
 * @param options.mode 'live' (default) or 'once'
 */
export function useMissionHandlerClusterDocument(clusterName, options) {
    return useDocument(MissionHandlerClusterDocument, [clusterName], options);
}

export function MissionHandlerClusterCollection() {
    return createCollectionApi(paths.missionHandlerClusterCollection)();
}

/**
 * Hook for subscribing to or once-fetching a MissionHandlerCluster collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useMissionHandlerClusterCollection(
    constraints = [],
    options = {},
) {
    return useCollection(
        MissionHandlerClusterCollection,
        [],
        constraints,
        options,
    );
}

export const MissionHandlerClusterCollectionGroup = createCollectionGroupApi(
    "missionHandlerClusters",
);

/**
 * Hook for subscribing to or once-fetching a MissionHandlerCluster collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useMissionHandlerClusterCollectionGroup(
    constraints = [],
    options = {},
) {
    return useCollectionGroup(
        MissionHandlerClusterCollectionGroup,
        constraints,
        options,
    );
}

// ---- Models Resources ----

export function ModelsDocument(modelId) {
    return createDocumentApi(paths.modelsDocument)(modelId);
}

/**
 * Hook for subscribing to or once-fetching a single Models document.
 *
 * @param modelId
 * @param options.mode 'live' (default) or 'once'
 */
export function useModelsDocument(modelId, options) {
    return useDocument(ModelsDocument, [modelId], options);
}

export function ModelsCollection() {
    return createCollectionApi(paths.modelsCollection)();
}

/**
 * Hook for subscribing to or once-fetching a Models collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useModelsCollection(constraints = [], options = {}) {
    return useCollection(ModelsCollection, [], constraints, options);
}

export const ModelsCollectionGroup = createCollectionGroupApi("models");

/**
 * Hook for subscribing to or once-fetching a Models collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useModelsCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(ModelsCollectionGroup, constraints, options);
}

// ---- Organization Resources ----

export function OrganizationDocument(orgId) {
    return createDocumentApi(paths.organizationDocument)(orgId);
}

/**
 * Hook for subscribing to or once-fetching a single Organization document.
 *
 * @param orgId
 * @param options.mode 'live' (default) or 'once'
 */
export function useOrganizationDocument(orgId, options) {
    return useDocument(OrganizationDocument, [orgId], options);
}

export function OrganizationCollection() {
    return createCollectionApi(paths.organizationCollection)();
}

/**
 * Hook for subscribing to or once-fetching a Organization collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useOrganizationCollection(constraints = [], options = {}) {
    return useCollection(OrganizationCollection, [], constraints, options);
}

export const OrganizationCollectionGroup =
    createCollectionGroupApi("organizations");

/**
 * Hook for subscribing to or once-fetching a Organization collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useOrganizationCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(
        OrganizationCollectionGroup,
        constraints,
        options,
    );
}

// ---- Overlay Resources ----

export function OverlayDocument(orgId, locId, measurementId, overlayId) {
    return createDocumentApi(paths.overlayDocument)(
        orgId,
        locId,
        measurementId,
        overlayId,
    );
}

/**
 * Hook for subscribing to or once-fetching a single Overlay document.
 *
 * @param orgId
 * @param locId
 * @param measurementId
 * @param overlayId
 * @param options.mode 'live' (default) or 'once'
 */
export function useOverlayDocument(
    orgId,
    locId,
    measurementId,
    overlayId,
    options,
) {
    return useDocument(
        OverlayDocument,
        [orgId, locId, measurementId, overlayId],
        options,
    );
}

export function OverlayCollection(orgId, locId, measurementId) {
    return createCollectionApi(paths.overlayCollection)(
        orgId,
        locId,
        measurementId,
    );
}

/**
 * Hook for subscribing to or once-fetching a Overlay collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param measurementId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useOverlayCollection(
    orgId,
    locId,
    measurementId,
    constraints = [],
    options = {},
) {
    return useCollection(
        OverlayCollection,
        [orgId, locId, measurementId],
        constraints,
        options,
    );
}

export const OverlayCollectionGroup = createCollectionGroupApi("overlays");

/**
 * Hook for subscribing to or once-fetching a Overlay collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useOverlayCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(OverlayCollectionGroup, constraints, options);
}

// ---- Payload Resources ----

export function PayloadDocument(payloadId) {
    return createDocumentApi(paths.payloadDocument)(payloadId);
}

/**
 * Hook for subscribing to or once-fetching a single Payload document.
 *
 * @param payloadId
 * @param options.mode 'live' (default) or 'once'
 */
export function usePayloadDocument(payloadId, options) {
    return useDocument(PayloadDocument, [payloadId], options);
}

export function PayloadCollection() {
    return createCollectionApi(paths.payloadCollection)();
}

/**
 * Hook for subscribing to or once-fetching a Payload collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function usePayloadCollection(constraints = [], options = {}) {
    return useCollection(PayloadCollection, [], constraints, options);
}

export const PayloadCollectionGroup = createCollectionGroupApi("payloads");

/**
 * Hook for subscribing to or once-fetching a Payload collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function usePayloadCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(PayloadCollectionGroup, constraints, options);
}

// ---- Result Resources ----

export function ResultDocument(orgId, locId, missionId, resultId) {
    return createDocumentApi(paths.resultDocument)(
        orgId,
        locId,
        missionId,
        resultId,
    );
}

/**
 * Hook for subscribing to or once-fetching a single Result document.
 *
 * @param orgId
 * @param locId
 * @param missionId
 * @param resultId
 * @param options.mode 'live' (default) or 'once'
 */
export function useResultDocument(orgId, locId, missionId, resultId, options) {
    return useDocument(
        ResultDocument,
        [orgId, locId, missionId, resultId],
        options,
    );
}

export function ResultCollection(orgId, locId, missionId) {
    return createCollectionApi(paths.resultCollection)(orgId, locId, missionId);
}

/**
 * Hook for subscribing to or once-fetching a Result collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param missionId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useResultCollection(
    orgId,
    locId,
    missionId,
    constraints = [],
    options = {},
) {
    return useCollection(
        ResultCollection,
        [orgId, locId, missionId],
        constraints,
        options,
    );
}

export const ResultCollectionGroup = createCollectionGroupApi("results");

/**
 * Hook for subscribing to or once-fetching a Result collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useResultCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(ResultCollectionGroup, constraints, options);
}

// ---- Routeplanner Resources ----

export function RouteplannerDocument(docId) {
    return createDocumentApi(paths.routeplannerDocument)(docId);
}

/**
 * Hook for subscribing to or once-fetching a single Routeplanner document.
 *
 * @param docId
 * @param options.mode 'live' (default) or 'once'
 */
export function useRouteplannerDocument(docId, options) {
    return useDocument(RouteplannerDocument, [docId], options);
}

export function RouteplannerCollection() {
    return createCollectionApi(paths.routeplannerCollection)();
}

/**
 * Hook for subscribing to or once-fetching a Routeplanner collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useRouteplannerCollection(constraints = [], options = {}) {
    return useCollection(RouteplannerCollection, [], constraints, options);
}

export const RouteplannerCollectionGroup =
    createCollectionGroupApi("routeplanners");

/**
 * Hook for subscribing to or once-fetching a Routeplanner collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useRouteplannerCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(
        RouteplannerCollectionGroup,
        constraints,
        options,
    );
}

// ---- Routine Resources ----

export function RoutineDocument(orgId, locId, routineId) {
    return createDocumentApi(paths.routineDocument)(orgId, locId, routineId);
}

/**
 * Hook for subscribing to or once-fetching a single Routine document.
 *
 * @param orgId
 * @param locId
 * @param routineId
 * @param options.mode 'live' (default) or 'once'
 */
export function useRoutineDocument(orgId, locId, routineId, options) {
    return useDocument(RoutineDocument, [orgId, locId, routineId], options);
}

export function RoutineCollection(orgId, locId) {
    return createCollectionApi(paths.routineCollection)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a Routine collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useRoutineCollection(
    orgId,
    locId,
    constraints = [],
    options = {},
) {
    return useCollection(
        RoutineCollection,
        [orgId, locId],
        constraints,
        options,
    );
}

export const RoutineCollectionGroup = createCollectionGroupApi("routines");

/**
 * Hook for subscribing to or once-fetching a Routine collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useRoutineCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(RoutineCollectionGroup, constraints, options);
}

// ---- Tags Resources ----

export function TagsDocument() {
    return createDocumentApi(paths.tagsDocument)();
}

/**
 * Hook for subscribing to or once-fetching a single Tags document.
 *

 * @param options.mode 'live' (default) or 'once'
 */
export function useTagsDocument(options) {
    return useDocument(TagsDocument, [], options);
}

// ---- Task Resources ----

export function TaskDocument(orgId, locId, taskId) {
    return createDocumentApi(paths.taskDocument)(orgId, locId, taskId);
}

/**
 * Hook for subscribing to or once-fetching a single Task document.
 *
 * @param orgId
 * @param locId
 * @param taskId
 * @param options.mode 'live' (default) or 'once'
 */
export function useTaskDocument(orgId, locId, taskId, options) {
    return useDocument(TaskDocument, [orgId, locId, taskId], options);
}

export function TaskCollection(orgId, locId) {
    return createCollectionApi(paths.taskCollection)(orgId, locId);
}

/**
 * Hook for subscribing to or once-fetching a Task collection,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param orgId
 * @param locId
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useTaskCollection(
    orgId,
    locId,
    constraints = [],
    options = {},
) {
    return useCollection(TaskCollection, [orgId, locId], constraints, options);
}

export const TaskCollectionGroup = createCollectionGroupApi("tasks");

/**
 * Hook for subscribing to or once-fetching a Task collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useTaskCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(TaskCollectionGroup, constraints, options);
}

// ---- UnauthorizedAccess Resources ----

export function UnauthorizedAccessDocument(docId) {
    return createDocumentApi(paths.unauthorizedAccessDocument)(docId);
}

/**
 * Hook for subscribing to or once-fetching a single UnauthorizedAccess document.
 *
 * @param docId
 * @param options.mode 'live' (default) or 'once'
 */
export function useUnauthorizedAccessDocument(docId, options) {
    return useDocument(UnauthorizedAccessDocument, [docId], options);
}

export function UnauthorizedAccessCollection() {
    return createCollectionApi(paths.unauthorizedAccessCollection)();
}

/**
 * Hook for subscribing to or once-fetching a UnauthorizedAccess collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useUnauthorizedAccessCollection(
    constraints = [],
    options = {},
) {
    return useCollection(
        UnauthorizedAccessCollection,
        [],
        constraints,
        options,
    );
}

export const UnauthorizedAccessCollectionGroup =
    createCollectionGroupApi("unauthorizedAccess");

/**
 * Hook for subscribing to or once-fetching a UnauthorizedAccess collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useUnauthorizedAccessCollectionGroup(
    constraints = [],
    options = {},
) {
    return useCollectionGroup(
        UnauthorizedAccessCollectionGroup,
        constraints,
        options,
    );
}

// ---- UnavailableDroneNames Resources ----

export function UnavailableDroneNamesDocument() {
    return createDocumentApi(paths.unavailableDroneNamesDocument)();
}

/**
 * Hook for subscribing to or once-fetching a single UnavailableDroneNames document.
 *

 * @param options.mode 'live' (default) or 'once'
 */
export function useUnavailableDroneNamesDocument(options) {
    return useDocument(UnavailableDroneNamesDocument, [], options);
}

// ---- UserFeedback Resources ----

export function UserFeedbackDocument(userId) {
    return createDocumentApi(paths.userFeedbackDocument)(userId);
}

/**
 * Hook for subscribing to or once-fetching a single UserFeedback document.
 *
 * @param userId
 * @param options.mode 'live' (default) or 'once'
 */
export function useUserFeedbackDocument(userId, options) {
    return useDocument(UserFeedbackDocument, [userId], options);
}

export function UserFeedbackCollection() {
    return createCollectionApi(paths.userFeedbackCollection)();
}

/**
 * Hook for subscribing to or once-fetching a UserFeedback collection,
 * returning either a list or a map (with document id as key) of documents.
 *

 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useUserFeedbackCollection(constraints = [], options = {}) {
    return useCollection(UserFeedbackCollection, [], constraints, options);
}

export const UserFeedbackCollectionGroup =
    createCollectionGroupApi("userFeedbacks");

/**
 * Hook for subscribing to or once-fetching a UserFeedback collection group,
 * returning either a list or a map (with document id as key) of documents.
 *
 * @param constraints Array of query constraints
 * @param options.mode 'live' (default) or 'once'
 * @param options.type 'list' (default) or 'map'
 */
export function useUserFeedbackCollectionGroup(constraints = [], options = {}) {
    return useCollectionGroup(
        UserFeedbackCollectionGroup,
        constraints,
        options,
    );
}

// ---- Versions Resources ----

export function VersionsDocument() {
    return createDocumentApi(paths.versionsDocument)();
}

/**
 * Hook for subscribing to or once-fetching a single Versions document.
 *

 * @param options.mode 'live' (default) or 'once'
 */
export function useVersionsDocument(options) {
    return useDocument(VersionsDocument, [], options);
}
